import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FilePlus, ShieldCheck } from "lucide-react";
import ScanForm from './components/ScanForm';
import ScanList from './components/ScanList';
import { useScanUpload } from './hooks/useScanUpload';

const AddFilePage = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  const {
    scans,
    description,
    loading,
    handleFileChange,
    handleDescriptionChange,
    handleAddScan,
    handleRemoveScan,
    handleSubmit
  } = useScanUpload(uhid_no);

  return (
    <div className="min-h-screen w-full bg-[#F8FAFC] pb-20">
      {/* Decorative Header */}
      <div className="h-64 w-full bg-gradient-to-br from-teal-800 via-teal-700 to-cyan-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-4 border-white rounded-full animate-pulse" />
          <div className="absolute bottom-20 right-20 w-60 h-60 border-8 border-white rounded-full animate-pulse delay-700" />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 h-full flex flex-col justify-end pb-12">
          <button 
            onClick={() => navigate(-1)}
            className="w-fit flex items-center gap-2 text-teal-100 hover:text-white mb-8 transition-colors font-medium group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Patient Record
          </button>
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md w-fit px-4 py-1.5 rounded-full border border-white/20">
                <ShieldCheck className="w-4 h-4 text-teal-300" />
                <span className="text-xs font-black text-white uppercase tracking-widest">Secure Document Vault</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                Upload Scans & Documents
              </h1>
            </div>
            
            <div className="bg-white/10 backdrop-blur-xl p-5 rounded-3xl border border-white/20 min-w-[200px]">
              <span className="text-[10px] text-teal-200 font-black uppercase tracking-[0.2em] mb-1 block">Patient reference</span>
              <div className="text-2xl font-mono font-bold text-white uppercase">{uhid_no}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 -mt-10">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
          {/* Left Column - Form */}
          <div className="xl:col-span-4 sticky top-10">
            <div className="bg-white p-2 rounded-[2.5rem] shadow-2xl border border-slate-100">
              <ScanForm 
                description={description}
                handleDescriptionChange={handleDescriptionChange}
                handleFileChange={handleFileChange}
                handleAddScan={handleAddScan}
                handleSubmit={handleSubmit}
                loading={loading}
              />
            </div>
            
            <div className="mt-8 px-6 py-4 bg-teal-50 rounded-2xl border border-teal-100 flex items-center gap-4">
              <div className="p-3 bg-teal-500 rounded-xl">
                <FilePlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-teal-900 text-sm">Batch Processing</h4>
                <p className="text-xs text-teal-700">Add multiple docs and upload at once</p>
              </div>
            </div>
          </div>

          {/* Right Column - Gallery */}
          <div className="xl:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-black text-slate-800">Document Gallery</h2>
                <div className="bg-slate-200 text-slate-600 px-3 py-1 rounded-full text-xs font-black">
                  {scans.length} FILES
                </div>
              </div>
            </div>
            
            <ScanList 
              scans={scans}
              handleRemoveScan={handleRemoveScan}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFilePage;
